'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { ENGINE_PATH_PREFIX } from '@gnsx/genesys.js';
import { runInAction } from 'mobx';
import { destroy, detach, getParent, getParentOfType, hasParent, isAlive, isStateTreeNode, t } from 'mobx-state-tree';
import { moveItem } from 'mobx-utils';

import { editorStore } from '../editorStore';


import { adjustMoveInsertIndex, canParentSceneTreeNode, canReparentSceneTreeNode, filterTopLevelSceneTreeNodes, sceneTreeNodeContains } from './sceneTreeGuards';

import type { OutlinerNodeData } from '../../components/tree/shared/types/tree.types';
import type { TreeInstance } from '@headless-tree/core';
import type { IAnyModelType, Instance } from 'mobx-state-tree';

import { type EditorObjectData } from '@/app/editor/core/editorClassData';
import { findActorFromTarget, getPrefabLabel, getPrefabName, hideActorInEditor, isPrefabInstance, lockActorInEditor } from '@/editor/core/objectHelpers';
import { getSceneUndoRedo } from '@/editor/core/undoRedo';

/**
 * SceneTreeNode
 */
export const SceneTreeNode = t
  .model('SceneTreeNode', {
    id: t.identifier,
    name: t.string,
    children: t.array(t.late((): IAnyModelType => SceneTreeNode)),
    isRoot: t.optional(t.boolean, false),
    isExpanded: t.optional(t.boolean, true),
    isSelected: t.optional(t.boolean, false),
    isVisible: t.optional(t.boolean, true),
    isLocked: t.optional(t.boolean, false),
    isPrefab: t.optional(t.boolean, false),
    nodeType: t.optional(t.enumeration(['scene', 'folder', 'actor']), 'actor'),
  })
  .volatile(() => ({
    actor: undefined as ENGINE.Actor | undefined,
  }))
  .views(self => ({
    get asActor() { return self.actor; },
    get prefabName() { return getPrefabName(this.asActor); },
    get prefabLabel() { return getPrefabLabel(this.asActor); },
    get isEngineAsset() {
      return this.prefabName?.startsWith(ENGINE_PATH_PREFIX) ?? false;
    },
    get isFolder() { return self.nodeType === 'folder'; },
    get isScene() { return self.nodeType === 'scene'; },
    get isActorNode() { return self.nodeType === 'actor'; },
    get hasChildren() {
      if (!isAlive(self)) return false;
      return self.children.length > 0;
    },
    get canRename() { return self.nodeType !== 'scene'; },
    /** Check if any child actors are visible */
    get hasSomeChildrenVisible(): boolean {
      const checkVisible = (node: Instance<typeof SceneTreeNode>): boolean => {
        if (!isAlive(node)) return false;
        if (node.nodeType === 'actor' && node.isVisible) {
          return true;
        }
        // Folders: a visible folder counts as visible content (even if empty)
        if (node.nodeType === 'folder') {
          // If folder is not visible, it doesn't contribute to visibility
          if (!node.isVisible) {
            return false;
          }
          // If folder is visible, it counts as having visible content
          // (folders are containers and if visible, they're part of the visible structure)
          return true;
        }
        return node.children.some(child => checkVisible(child));
      };
      if (!isAlive(self)) return false;
      return self.children.some(child => checkVisible(child));
    },
    /** Check if any child actors are locked */
    get hasSomeChildrenLocked(): boolean {
      const checkLocked = (node: Instance<typeof SceneTreeNode>): boolean => {
        if (!isAlive(node)) return false;
        if (node.nodeType === 'actor' && node.isLocked) {
          return true;
        }
        return node.children.some(child => checkLocked(child));
      };
      if (!isAlive(self)) return false;
      return self.children.some(child => checkLocked(child));
    },
  }))
  .actions(self => ({
    init(target: ENGINE.Actor, reset: boolean = true) {
      // Only initialize actor data for actor nodes
      if (self.nodeType === 'actor') {
        self.actor = findActorFromTarget(target) ?? undefined;
        if (self.actor) {
          self.name = self.actor.name;
        }
        const editorData = self.actor?.editorData as EditorObjectData | undefined;
        self.isVisible = !editorData?.hidden;
        self.isLocked = editorData?.selectable === false;
        self.isPrefab = isPrefabInstance(self.asActor);
      }
    },
    setSelected(selected: boolean) {
      self.isSelected = selected;
    },
    addChild(childData: Instance<typeof SceneTreeNode>) {
      const newNode: Instance<typeof self> = SceneTreeNode.create(childData);
      self.children.push(newNode);
      return newNode;
    },
    remove() {
      const tree = getParentOfType(self, SceneTree);
      tree?.removeNode(self.id);
    },
    setName(newName: string) {
      self.name = newName;
    },
    setVisibilityInEditor(visible: boolean) {
      const undoRedo = getSceneUndoRedo();
      const nodeId = self.id;
      const currentVisibility = self.isVisible;

      // Don't create undo action if visibility isn't actually changing
      if (currentVisibility === visible) {
        return;
      }

      void undoRedo.recordAction(
        'Set Editor Visibility',
        () => {
          const node = editorStore.scene.outliner.getNodeById(nodeId);
          if (node) {
            node.setIsVisible(visible);
            node.asActor && hideActorInEditor(node.asActor, !visible);
          }
        },
        () => {
          const node = editorStore.scene.outliner.getNodeById(nodeId);
          if (node) {
            node.setIsVisible(currentVisibility);
            node.asActor && hideActorInEditor(node.asActor, !currentVisibility);
          }
        },
      );
    },
    toggleVisibilityInEditor() {
      this.setVisibilityInEditor(!self.isVisible);
    },
    setLocked(locked: boolean) {
      const undoRedo = getSceneUndoRedo();
      const nodeId = self.id;
      const currentLocked = self.isLocked;

      // Don't create undo action if lock state isn't actually changing
      if (currentLocked === locked) return;

      void undoRedo.recordAction(
        'Set Selection Lock',
        () => {
          const node = editorStore.scene.outliner.getNodeById(nodeId);
          if (node) {
            node.setIsLocked(locked);
            node.asActor && lockActorInEditor(node.asActor, locked);
          }
        },
        () => {
          const node = editorStore.scene.outliner.getNodeById(nodeId);
          if (node) {
            node.setIsLocked(currentLocked);
            node.asActor && lockActorInEditor(node.asActor, currentLocked);
          }
        },
      );
    },
    toggleLocked() {
      this.setLocked(!self.isLocked);
    },
    setExpanded(expanded: boolean) {
      self.isExpanded = expanded;
    },
    /** Set isVisible directly (used by undo/redo callbacks — does not create undo entries). */
    setIsVisible(visible: boolean) {
      self.isVisible = visible;
    },
    /** Set isLocked directly (used by undo/redo callbacks — does not create undo entries). */
    setIsLocked(locked: boolean) {
      self.isLocked = locked;
    },
    /** Recursively set visibility for all child actors and folders, grouped as one undo step. */
    setVisibilityRecursive(visible: boolean) {
      const undoRedo = getSceneUndoRedo();
      const isInsideGroup = undoRedo.isGroupOpen;
      if (!isInsideGroup) undoRedo.beginGroup(visible ? 'Show' : 'Hide');

      if (self.nodeType === 'actor' && self.asActor) {
        this.setVisibilityInEditor(visible);
      }
      if (self.nodeType === 'folder') {
        self.isVisible = visible;
      }
      self.children.forEach(child => child.setVisibilityRecursive(visible));

      if (!isInsideGroup) undoRedo.endGroup();
    },
    /** Recursively set lock state for all child actors, grouped as one undo step. */
    setLockedRecursive(locked: boolean) {
      const undoRedo = getSceneUndoRedo();
      const isInsideGroup = undoRedo.isGroupOpen;
      if (!isInsideGroup) undoRedo.beginGroup(locked ? 'Lock' : 'Unlock');

      if (self.nodeType === 'actor' && self.asActor) {
        this.setLocked(locked);
      }
      self.children.forEach(child => child.setLockedRecursive(locked));

      if (!isInsideGroup) undoRedo.endGroup();
    }
  }));
export interface ISceneTreeNode extends Instance<typeof SceneTreeNode> { }

export interface SceneTreeMoveResult {
  movedNodeIds: string[];
  skippedNodeIds: string[];
}


/**
 * SceneTree
 */

let _expandSyncTimer: ReturnType<typeof setTimeout> | null = null;

/** Debounce the expensive world-data export that runs after expand/collapse. */
const debouncedWorldDataSync = () => {
  if (_expandSyncTimer) clearTimeout(_expandSyncTimer);
  _expandSyncTimer = setTimeout(() => {
    _expandSyncTimer = null;
    editorStore.scene.updateWorldDataWithOutliner();
  }, 500);
};

export const SceneTree = t
  .model('SceneTree', {
    nodes: t.array(SceneTreeNode),
    nodeMap: t.map(t.safeReference(SceneTreeNode, { acceptsUndefined: false })),
    selectedNodes: t.array(t.safeReference(SceneTreeNode, { acceptsUndefined: false })),
    expandedNodes: t.map(t.safeReference(SceneTreeNode, { acceptsUndefined: false })),
    /** Bumped after hierarchy changes so OutlinerTreeView can rebuild headless-tree during render. */
    childrenOrderVersion: t.optional(t.number, 0),
  })
  .volatile(() => ({
    /** Cached tree instance for accessing tree methods and state */
    treeView: undefined as TreeInstance<OutlinerNodeData> | undefined,
  }))
  .views(self => ({
    get selectedNode() {
      return self.selectedNodes.length > 0 ? self.selectedNodes[0] : undefined;
    },
    get selectedNodeId() {
      return self.selectedNodes.length > 0 ? self.selectedNodes[0]?.id : undefined;
    },
    get selectedNodeIds() {
      return self.selectedNodes.map(node => node.id);
    },
    get actorNodeIds() {
      const ids = new Set<string>();
      for (const node of self.nodeMap.values()) {
        if (node.nodeType === 'actor') {
          ids.add(node.id);
        }
      }
      return ids;
    },
    get expandedNodeIds() {
      return Array.from(self.expandedNodes.keys());
    },
    get rootNode() { return self.nodes[0]; },
    getNodeById(id: string): ISceneTreeNode | undefined {
      return self.nodeMap.get(id);
    },
    _getNode(idOrNode: string | ISceneTreeNode): ISceneTreeNode | undefined {
      return typeof idOrNode === 'string' ? this.getNodeById(idOrNode) : idOrNode;
    },
    /** Find the first selected node in tree traversal order */
    findFirstSelectedNodeInTreeOrder(selectedActorIds: string[]): ISceneTreeNode | null {
      const selectedSet = new Set(selectedActorIds);
      const rootNode = this.rootNode;
      if (!rootNode) return null;

      const traverseInOrder = (node: ISceneTreeNode): ISceneTreeNode | null => {
        // Check if current node is one of the selected actors
        if (selectedSet.has(node.id)) {
          return node;
        }

        // Traverse children in order
        for (const child of node.children) {
          const result = traverseInOrder(child);
          if (result) return result;
        }

        return null;
      };

      return traverseInOrder(rootNode);
    },
  }))
  .actions(self => ({
    /** Register a node and all its descendants in nodeMap/expandedNodes. */
    _addToNodeMap(node: ISceneTreeNode) {
      self.nodeMap.put(node);
      if (node.isExpanded) {
        self.expandedNodes.put(node);
      }
      node.children.forEach(child => this._addToNodeMap(child));
    },
    /** Unregister a node and all its descendants from nodeMap/expandedNodes. */
    _removeFromNodeMap(node: ISceneTreeNode) {
      node.children.forEach(child => this._removeFromNodeMap(child));
      self.nodeMap.delete(node.id);
      self.expandedNodes.delete(node.id);
    },
  }))
  .actions(self => ({
    reset() {
      self.nodes.clear();
      self.selectedNodes.clear();
      self.nodeMap.clear();
      self.expandedNodes.clear();
      self.childrenOrderVersion = 0;
    },
    setTreeView(tree: TreeInstance<OutlinerNodeData> | undefined) {
      self.treeView = tree;
    },
    /** Increment version counter so the outliner observer re-renders after structural changes. */
    bumpVersion() {
      self.childrenOrderVersion += 1;
    },
    _resolveLiveNode(idOrNode: string | ISceneTreeNode): ISceneTreeNode | undefined {
      if (typeof idOrNode === 'string') return self.getNodeById(idOrNode);
      const existingNode = self.getNodeById(idOrNode.id);
      if (existingNode) return existingNode;
      return idOrNode;
    },
    _resolveParentNode(parentIdOrNode: string | ISceneTreeNode | null): ISceneTreeNode | null | undefined {
      if (parentIdOrNode == null) return null;
      if (typeof parentIdOrNode === 'string') return self.getNodeById(parentIdOrNode);
      return self.getNodeById(parentIdOrNode.id);
    },
    addNode(idOrNode: string | ISceneTreeNode, parentIdOrNode: string | ISceneTreeNode | null) {
      const node = this._resolveLiveNode(idOrNode);
      if (!node) return;
      const hasExplicitParent = parentIdOrNode != null;
      const parent = this._resolveParentNode(parentIdOrNode);

      if (hasExplicitParent && !parent) {
        const targetId = typeof parentIdOrNode === 'string' ? parentIdOrNode : parentIdOrNode.id;
        console.warn(`[SceneTree] Skipping reparent ${node.id} -> ${targetId}: target parent not found`);
        return;
      }

      if (node.isRoot && parent) {
        console.warn(`[SceneTree] Skipping reparent ${node.id} -> ${parent.id}: root node cannot be moved`);
        return;
      }

      if (!canParentSceneTreeNode(node, parent)) {
        const parentLabel = parent?.id ?? 'null';
        console.warn(`[SceneTree] Skipping reparent ${node.id} -> ${parentLabel}: invalid parent for ${node.nodeType}`);
        return;
      }

      if (parent) {
        if (!canReparentSceneTreeNode(node, parent)) {
          const reason = parent.id === node.id ? 'cannot parent a node to itself' : 'would create cycle';
          console.warn(`[SceneTree] Skipping reparent ${node.id} -> ${parent.id}: ${reason}`);
          return;
        }
      }
      self._removeFromNodeMap(node);
      if (hasParent(node)) {
        detach(node);
      }
      const children = parent ? parent.children : self.nodes;
      children.push(node);
      self._addToNodeMap(node);
      this.bumpVersion();
      return node;
    },
    _removeNodeInternal(idOrNode: string | ISceneTreeNode) {
      const node = self._getNode(idOrNode);
      if (!node) return false;
      self._removeFromNodeMap(node);
      // Remove from selectedNodes
      const selectedIndex = self.selectedNodes.findIndex(n => n.id === node.id);
      if (selectedIndex !== -1) {
        self.selectedNodes.splice(selectedIndex, 1);
      }
      destroy(node);
      return true;
    },
    removeNode(idOrNode: string | ISceneTreeNode) {
      const removed = this._removeNodeInternal(idOrNode);
      if (removed) {
        this.bumpVersion();
      }
    },
    removeNodes(ids: string[]) {
      let hasRemovedNodes = false;
      ids.forEach(id => {
        hasRemovedNodes = this._removeNodeInternal(id) || hasRemovedNodes;
      });
      if (hasRemovedNodes) {
        this.bumpVersion();
      }
    },
    selectNode(id: string | null, multiSelect: boolean = false) {
      runInAction(() => {

        if (id == null) {
          self.selectedNodes.forEach(node => node.setSelected(false));
          self.selectedNodes.clear();
          return;
        }

        const node = self.getNodeById(id);
        if (!node) return;

        const existingIndex = self.selectedNodes.findIndex(n => n.id === id);

        if (multiSelect && existingIndex !== -1) {
          node.setSelected(false);
          self.selectedNodes.splice(existingIndex, 1);
          return;
        }

        if (!multiSelect) {
          self.selectedNodes.forEach(n => n.setSelected(false));
          self.selectedNodes.clear();
        }

        if (existingIndex === -1) {
          node.setSelected(true);
          self.selectedNodes.push(node);
        }
      });
    },
    setExpandedNodeIds(updater: string[] | ((ids: string[]) => string[])) {
      const currentIds = self.expandedNodeIds;
      const itemIds = Array.isArray(updater) ? updater : updater(currentIds);

      // Instead of updating all nodes, only update the ones that changed
      const currentSet = new Set(currentIds);
      const newSet = new Set(itemIds);

      // Find nodes that should be collapsed (were expanded but not in new list)
      const toCollapse = currentIds.filter(id => !newSet.has(id));
      // Find nodes that should be expanded (are in new list but weren't expanded)
      const toExpand = itemIds.filter(id => !currentSet.has(id));

      // Collapse nodes that should be collapsed
      toCollapse.forEach(id => {
        const node = self.getNodeById(id);
        if (node) {
          node.setExpanded(false);
          self.expandedNodes.delete(id);
        }
      });

      // Expand nodes that should be expanded
      toExpand.forEach(id => {
        const node = self.getNodeById(id);
        if (node) {
          node.setExpanded(true);
          self.expandedNodes.put(node);
        }
      });

      // Sync to world.editorData so collapse state persists on save (debounced to
      // avoid full tree serialization on every rapid expand/collapse)
      debouncedWorldDataSync();
    },
    pruneExpandedNodeIds() {
      const currentIds = self.expandedNodeIds;
      const nextIds = currentIds.filter(id => {
        const node = self.getNodeById(id);
        if (!node) return false;
        if (node.nodeType === 'scene' || node.nodeType === 'folder') return true;
        if (node.nodeType === 'actor') return node.hasChildren;
        return false;
      });

      if (
        nextIds.length === currentIds.length &&
        nextIds.every((id, index) => id === currentIds[index])
      ) {
        return;
      }

      this.setExpandedNodeIds(nextIds);
    },
    setSelectedNodeIds(updater: string[] | ((ids: string[]) => string[])) {
      const currentIds = self.selectedNodeIds;
      const itemIds = Array.isArray(updater) ? updater : updater(currentIds);

      // Early exit when the ordered selection is already identical
      if (
        itemIds.length === currentIds.length &&
        itemIds.every((id, i) => id === currentIds[i])
      ) {
        if (editorStore.inspectorPanel.showSceneSettingsView) {
          editorStore.inspectorPanel.setShowSceneSettingsView(false);
          editorStore.clearSceneSettingsPreviousTargetSnapshot();
        }
        return;
      }

      // Diff-based update: only touch nodes that actually changed
      const currentSet = new Set(currentIds);
      const nextSet = new Set(itemIds);

      // Deselect nodes that are leaving the selection
      for (const id of currentIds) {
        if (!nextSet.has(id)) {
          self.getNodeById(id)?.setSelected(false);
        }
      }

      // Rebuild selectedNodes array in the new order, selecting only newly added nodes
      self.selectedNodes.clear();
      for (const id of itemIds) {
        const node = self.getNodeById(id);
        if (node) {
          if (!currentSet.has(id)) {
            node.setSelected(true);
          }
          self.selectedNodes.push(node);
        }
      }

      // Sync tree focus with selection - handles external selection (e.g., from viewport)
      if (itemIds.length > 0 && self.treeView) {
        const item = self.treeView.getItemInstance(itemIds[0]);
        if (item && !item.isFocused()) {
          item.setFocused();
        }
      }

      this.updateSceneSelection();
    },
    pruneSelectionToLiveNodes() {
      this.setSelectedNodeIds(currentIds => currentIds.filter(id => self.getNodeById(id) != null));
    },
    // Sync selection back to scene store's editable objects
    updateSceneSelection() {
      const editorScene = editorStore.scene;

      // Check if we're selecting the scene root
      const sceneRootNode = self.selectedNodes.find(node => node.isRoot);
      if (sceneRootNode && self.selectedNodes.length === 1) {
        editorScene.selectSceneRoot();
        return;
      }

      // Get all actors from selected nodes
      const selectedActors = self.selectedNodes
        .map(node => node.asActor)
        .filter(actor => actor !== null && actor !== undefined);

      // Early exit if selection hasn't changed (Set-based O(N) check, avoids sort)
      const currentObjects = editorScene.selectedObjects;
      if (selectedActors.length === currentObjects.length) {
        const currentIdSet = new Set(currentObjects.map(obj => obj.id));
        if (selectedActors.every(a => currentIdSet.has(a.uuid))) {
          return;
        }
      }

      if (selectedActors.length > 0) {
        editorScene.selectObjects(selectedActors, false, true);
      } else {
        editorScene.deselectObjects();
      }
    },
    deselectActorNodes() {
      // Only deselect and remove actor nodes from selection
      const actorNodes = self.selectedNodes.filter(node => node.nodeType === 'actor');
      actorNodes.forEach(node => {
        node.setSelected(false);
        const index = self.selectedNodes.indexOf(node);
        if (index !== -1) {
          self.selectedNodes.splice(index, 1);
        }
      });
    },
    loadSceneRoot(world: ENGINE.World): ISceneTreeNode {
      if (self.rootNode) {
        console.error('[SceneTree] createSceneRoot: Root node already exists');
        return self.rootNode;
      }
      const sceneName = editorStore.project.sceneName;

      return this.addNode(SceneTreeNode.create({
        id: world.uuid,
        name: sceneName ? `${sceneName} (Scene)` : (world.name ?? 'Scene'),
        children: [],
        isRoot: true,
        nodeType: 'scene',
      }), null)!;
    },
    /** Create a new folder in the tree */
    createFolder(name?: string, selectedItemIds?: string[], parentNode?: ISceneTreeNode) {
      const folderNode = this.addNode(SceneTreeNode.create({
        id: `folder-${ENGINE.shortUUID()}`,
        name: name || this.generateUniqueFolderName(),
        isRoot: false,
        isExpanded: true,
        isSelected: true,
        nodeType: 'folder',
      }), parentNode ?? self.rootNode);

      if (!folderNode) return;

      // Move selected actors to the new folder if any
      if (selectedItemIds && selectedItemIds.length > 0) {
        selectedItemIds.forEach(actorId => {
          this.reparentNode(actorId, folderNode);
        });
      }
      return folderNode;
    },

    /** Remove a folder from the tree
     * @param removeFolderOnly - If true, moves children to parent instead of deleting them recursively
     */
    removeFolder(folderId: string, removeFolderOnly: boolean = false) {
      const folder = self.getNodeById(folderId);
      if (!folder || !folder.isFolder) return;

      if (removeFolderOnly) {
        // Move all children to parent folder (or root if no parent)
        const parentFolder = this.findParentNode(folderId);
        const targetParent = parentFolder ?? self.rootNode;
        if (!targetParent) return;

        const childIds = folder.children.map(child => child.id);
        childIds.forEach(childId => {
          const childNode = self.getNodeById(childId);
          if (!childNode) return;
          if (childNode.nodeType === 'actor' || childNode.nodeType === 'folder') {
            this.reparentNode(childId, targetParent.id);
          }
        });

        const liveFolder = self.getNodeById(folderId);
        if (!liveFolder) return;
        if (liveFolder.children.length > 0) {
          console.warn(`[SceneTree] Skipping removeFolderOnly(${folderId}): folder still has children after reparent`);
          return;
        }

        this.removeNode(folderId);
      } else {
        // Recursively delete all nested folders and their contents
        const actorsToDelete = new Map<string, ENGINE.Actor>();
        const nestedFolderIds: string[] = [];
        const visitedNodeIds = new Set<string>();

        const collectDescendants = (nodeId: string) => {
          const node = self.getNodeById(nodeId);
          if (!node || visitedNodeIds.has(node.id)) return;
          visitedNodeIds.add(node.id);

          const childIds = node.children.map(child => child.id);
          childIds.forEach(childId => {
            const child = self.getNodeById(childId);
            if (!child) return;
            if (child.nodeType === 'actor' && child.asActor) {
              actorsToDelete.set(child.id, child.asActor);
            }
            collectDescendants(child.id);
            if (child.nodeType === 'folder') {
              nestedFolderIds.push(child.id);
            }
          });
        };

        collectDescendants(folderId);

        if (actorsToDelete.size > 0) {
          editorStore.scene.removeObjects(...actorsToDelete.values());
        }

        for (let i = nestedFolderIds.length - 1; i >= 0; i--) {
          this.removeNode(nestedFolderIds[i]);
        }

        this.removeNode(folderId);
      }
      this.pruneSelectionToLiveNodes();
    },
    /** Rename a folder in the tree */
    renameFolder(folderIdOrNode: string | ISceneTreeNode, newName: string) {
      const folderNode = typeof folderIdOrNode === 'string' ? self.getNodeById(folderIdOrNode) : folderIdOrNode;
      if (folderNode && folderNode.isFolder) {
        folderNode.setName(newName);
      }
    },
    reparentNode(idOrNode: string | ISceneTreeNode, targetIdOrNode: string | ISceneTreeNode | null) {
      const sourceNode = this._resolveLiveNode(idOrNode);
      if (!sourceNode) return;
      return this.addNode(sourceNode, targetIdOrNode ? targetIdOrNode : self.rootNode);
    },
    /** Returns the parent ID and child index of a node, or null if the node has no parent. */
    getChildIndex(nodeId: string): { parentId: string; index: number } | null {
      const parent = this.findParentNode(nodeId);
      if (!parent) return null;
      const index = parent.children.findIndex(c => c.id === nodeId);
      if (index === -1) return null;
      return { parentId: parent.id, index };
    },
    /** Moves a node to a specific index within a parent's children. Reparents if necessary. */
    setChildIndex(nodeId: string, parentId: string, index: number) {
      const node = this._resolveLiveNode(nodeId);
      const parent = self.getNodeById(parentId);
      if (!node || !parent) return;

      const currentParent = this.findParentNode(nodeId);
      if (currentParent?.id !== parentId) {
        this.addNode(node, parent);
      }

      const currentIndex = parent.children.findIndex(c => c.id === nodeId);
      if (currentIndex !== -1 && currentIndex !== index) {
        const clampedIndex = Math.min(index, parent.children.length - 1);
        moveItem(parent.children, currentIndex, clampedIndex);
        this.bumpVersion();
      }
    },
    getTopLevelNodes(nodeIds: string[]): ISceneTreeNode[] {
      const uniqueNodes = Array.from(new Set(nodeIds))
        .map(id => self.getNodeById(id))
        .filter((node): node is ISceneTreeNode => node != null);

      return filterTopLevelSceneTreeNodes(uniqueNodes, (nodeId, possibleAncestorId) =>
        this.isDescendantOf(nodeId, possibleAncestorId)
      );
    },
    findClosestFolderOrSceneParent(idOrNode: string | ISceneTreeNode): ISceneTreeNode | null {
      const node = this._resolveLiveNode(idOrNode);
      if (!node) return self.rootNode ?? null;
      if (node.nodeType === 'folder' || node.nodeType === 'scene') {
        return node;
      }

      let currentParent = this.findParentNode(node.id);
      while (currentParent) {
        if (currentParent.nodeType === 'folder' || currentParent.nodeType === 'scene') {
          return currentParent;
        }
        currentParent = this.findParentNode(currentParent.id);
      }

      return self.rootNode ?? null;
    },
    /** Find the parent node of a given node */
    findParentNode(nodeId: string): ISceneTreeNode | null {
      const targetNode = self.getNodeById(nodeId);
      if (targetNode && hasParent(targetNode)) {
        let cur: unknown = targetNode;
        while (hasParent(cur)) {
          const p = getParent(cur);
          if (p == null) {
            break;
          }
          if (isStateTreeNode(p)) {
            const candidate = p as ISceneTreeNode;
            const children = candidate.children;
            if (children?.some((c: ISceneTreeNode) => c.id === nodeId)) {
              return candidate;
            }
          }
          cur = p;
        }
      }

      const findParentRecursive = (node: ISceneTreeNode, targetId: string, visited: Set<string>): ISceneTreeNode | null => {
        if (visited.has(node.id)) return null;
        visited.add(node.id);
        for (const child of node.children) {
          if (child.id === targetId) {
            return node;
          }
          const result = findParentRecursive(child, targetId, visited);
          if (result) return result;
        }
        return null;
      };

      const rootNode = self.rootNode;
      return rootNode ? findParentRecursive(rootNode, nodeId, new Set()) : null;
    },
    /**
     * Returns true when `nodeId` is contained anywhere under `possibleAncestorId`.
     */
    isDescendantOf(nodeId: string, possibleAncestorId: string): boolean {
      if (nodeId === possibleAncestorId) return false;
      let currentParent = this.findParentNode(nodeId);
      const visited = new Set<string>();
      while (currentParent) {
        if (visited.has(currentParent.id)) return false;
        visited.add(currentParent.id);
        if (currentParent.id === possibleAncestorId) return true;
        currentParent = this.findParentNode(currentParent.id);
      }
      return false;
    },
    scrollToSelected() {
      const selectedNodeIds = self.selectedNodeIds;

      if (selectedNodeIds && selectedNodeIds.length > 0 && self.treeView) {
        const lastSelectedId = selectedNodeIds[selectedNodeIds.length - 1];

        // First, expand all parent folders by walking up the MST tree
        // This ensures the item will be in the rendered tree (getItems())
        const parentsToExpand: string[] = [];
        let currentNode = self.getNodeById(lastSelectedId);

        while (currentNode) {
          const parentNode = this.findParentNode(currentNode.id);
          if (parentNode && (parentNode.nodeType === 'folder' || parentNode.nodeType === 'actor')) {
            parentsToExpand.push(parentNode.id);
          }
          currentNode = parentNode ?? undefined;
        }

        // Expand all parent folders
        if (parentsToExpand.length > 0) {
          const currentExpandedIds = self.expandedNodeIds;
          const newExpandedIds = [...new Set([...currentExpandedIds, ...parentsToExpand])];
          this.setExpandedNodeIds(newExpandedIds);
        }

        // Now get the item instance and scroll to it
        // Use setTimeout to ensure the tree has been rebuilt after expansion
        setTimeout(() => {
          const item = self.treeView?.getItemInstance(lastSelectedId);
          if (!item) return;
          item.scrollTo({ behavior: 'smooth', block: 'center' });
        }, 0);
      }
    },
    /** Move node(s) to be positioned before the target node */
    moveBeforeNode(sourceIdOrNodes: string | ISceneTreeNode | (string | ISceneTreeNode)[], targetIdOrNode: string | ISceneTreeNode): SceneTreeMoveResult {
      const targetNode = typeof targetIdOrNode === 'string' ? self.getNodeById(targetIdOrNode) : targetIdOrNode;
      if (!targetNode || targetNode.isRoot) return { movedNodeIds: [], skippedNodeIds: [] };

      // Handle both single source and array of sources
      const sources = Array.isArray(sourceIdOrNodes) ? sourceIdOrNodes : [sourceIdOrNodes];
      const sourceNodes = sources
        .map(sourceIdOrNode => this._resolveLiveNode(sourceIdOrNode))
        .filter(node => node !== undefined) as ISceneTreeNode[];

      if (sourceNodes.length === 0) return { movedNodeIds: [], skippedNodeIds: [] };

      const targetParent = this.findParentNode(targetNode.id);
      if (!targetParent) return { movedNodeIds: [], skippedNodeIds: [] };
      const movedNodeIds = new Set<string>();
      const skippedNodeIds = new Set<string>();

      // Reparent all source nodes to target parent if needed
      const validSourceNodes = sourceNodes.filter(sourceNode => {
        if (sourceNode.id === targetNode.id || sourceNode.isRoot) {
          skippedNodeIds.add(sourceNode.id);
          return false;
        }
        if (sceneTreeNodeContains(sourceNode, targetParent.id)) {
          console.warn(`[SceneTree] Skipping move ${sourceNode.id} before ${targetNode.id}: would create cycle`);
          skippedNodeIds.add(sourceNode.id);
          return false;
        }
        if (sourceNode.nodeType === 'folder' && targetParent.nodeType === 'actor') {
          console.warn(`[SceneTree] Skipping move ${sourceNode.id} before ${targetNode.id}: folders cannot be parented under actors`);
          skippedNodeIds.add(sourceNode.id);
          return false;
        }
        return true;
      });

      validSourceNodes.forEach(sourceNode => {
        const sourceParent = this.findParentNode(sourceNode.id);
        if (sourceParent?.id !== targetParent.id) {
          const movedNode = this.addNode(sourceNode, targetParent);
          if (!movedNode) {
            skippedNodeIds.add(sourceNode.id);
            return;
          }
          movedNodeIds.add(sourceNode.id);
        }
      });

      // Position all source nodes before the target node
      const targetIndex = targetParent.children.findIndex((child: ISceneTreeNode) => child.id === targetNode.id);

      if (targetIndex !== -1) {
        runInAction(() => {
          // Get current indices of all source nodes and sort them to maintain relative order
          const sourceIndicesWithNodes = validSourceNodes
            .map(sourceNode => ({
              node: sourceNode,
              currentIndex: targetParent.children.findIndex((child: ISceneTreeNode) => child.id === sourceNode.id)
            }))
            .filter(item => item.currentIndex !== -1)
            .sort((a, b) => a.currentIndex - b.currentIndex);

          // Calculate the insertion position before the target
          let insertPosition = targetParent.children.findIndex((child: ISceneTreeNode) => child.id === targetNode.id);

          // Move each source node one by one, maintaining their relative order
          sourceIndicesWithNodes.forEach((item, index) => {
            const currentIndex = targetParent.children.findIndex((child: ISceneTreeNode) => child.id === item.node.id);
            if (currentIndex !== -1) {
              const desiredPosition = insertPosition + index;
              const adjustedDesiredPosition = adjustMoveInsertIndex(currentIndex, desiredPosition);

              if (currentIndex !== adjustedDesiredPosition) {
                moveItem(targetParent.children, currentIndex, adjustedDesiredPosition);
                movedNodeIds.add(item.node.id);

                // Update insertPosition for next iteration if needed
                if (currentIndex < insertPosition) {
                  insertPosition--;
                }
                this.bumpVersion();
              }
            }
          });
        });
      }
      return {
        movedNodeIds: Array.from(movedNodeIds),
        skippedNodeIds: Array.from(skippedNodeIds),
      };
    },
    /** Move node(s) to be positioned after the target node, or to index 0 if target is null */
    moveBehindNode(sourceIdOrNodes: string | ISceneTreeNode | (string | ISceneTreeNode)[], targetIdOrNode: string | ISceneTreeNode | null): SceneTreeMoveResult {
      const targetNode = targetIdOrNode ? (typeof targetIdOrNode === 'string' ? self.getNodeById(targetIdOrNode) : targetIdOrNode) : null;
      const movedNodeIds = new Set<string>();
      const skippedNodeIds = new Set<string>();

      // Handle both single source and array of sources
      const sources = Array.isArray(sourceIdOrNodes) ? sourceIdOrNodes : [sourceIdOrNodes];
      const sourceNodes = sources
        .map(sourceIdOrNode => this._resolveLiveNode(sourceIdOrNode))
        .filter(node => node !== undefined) as ISceneTreeNode[];

      if (sourceNodes.length === 0) {
        return { movedNodeIds: [], skippedNodeIds: [] };
      }

      // If target is null, move to index 0 of root node
      if (!targetNode) {
        const rootNode = self.rootNode;
        if (!rootNode) return { movedNodeIds: [], skippedNodeIds: [] };

        // Reparent all source nodes to root if needed
        const validSourceNodes = sourceNodes.filter(sourceNode => {
          if (sourceNode.id === rootNode.id || sourceNode.isRoot) {
            skippedNodeIds.add(sourceNode.id);
            return false;
          }
          return true;
        });

        validSourceNodes.forEach(sourceNode => {
          const sourceParent = this.findParentNode(sourceNode.id);
          if (sourceParent?.id !== rootNode.id) {
            const movedNode = this.addNode(sourceNode, rootNode);
            if (!movedNode) {
              skippedNodeIds.add(sourceNode.id);
              return;
            }
            movedNodeIds.add(sourceNode.id);
          }
        });

        // Move all source nodes to index 0, maintaining their relative order
        runInAction(() => {
          // Get current indices of all source nodes and sort them to maintain relative order
          const sourceIndicesWithNodes = validSourceNodes
            .map(sourceNode => ({
              node: sourceNode,
              currentIndex: rootNode.children.findIndex((child: ISceneTreeNode) => child.id === sourceNode.id)
            }))
            .filter(item => item.currentIndex !== -1)
            .sort((a, b) => a.currentIndex - b.currentIndex);

          // Move each source node to the beginning, maintaining their relative order
          sourceIndicesWithNodes.reverse().forEach((item, index) => {
            const currentIndex = rootNode.children.findIndex((child: ISceneTreeNode) => child.id === item.node.id);
            if (currentIndex !== -1 && currentIndex !== 0) {
              moveItem(rootNode.children, currentIndex, 0);
              movedNodeIds.add(item.node.id);
              this.bumpVersion();
            }
          });
        });
        return {
          movedNodeIds: Array.from(movedNodeIds),
          skippedNodeIds: Array.from(skippedNodeIds),
        };
      }

      const targetParent = this.findParentNode(targetNode.id);
      if (!targetParent) {
        return { movedNodeIds: [], skippedNodeIds: [] };
      }

      // Reparent all source nodes to target parent if needed
      const validSourceNodes = sourceNodes.filter(sourceNode => {
        if (sourceNode.id === targetNode.id || sourceNode.isRoot) {
          skippedNodeIds.add(sourceNode.id);
          return false;
        }
        if (sceneTreeNodeContains(sourceNode, targetParent.id)) {
          console.warn(`[SceneTree] Skipping move ${sourceNode.id} after ${targetNode.id}: would create cycle`);
          skippedNodeIds.add(sourceNode.id);
          return false;
        }
        if (sourceNode.nodeType === 'folder' && targetParent.nodeType === 'actor') {
          console.warn(`[SceneTree] Skipping move ${sourceNode.id} after ${targetNode.id}: folders cannot be parented under actors`);
          skippedNodeIds.add(sourceNode.id);
          return false;
        }
        return true;
      });

      validSourceNodes.forEach(sourceNode => {
        const sourceParent = this.findParentNode(sourceNode.id);
        if (sourceParent?.id !== targetParent.id) {
          const movedNode = this.addNode(sourceNode, targetParent);
          if (!movedNode) {
            skippedNodeIds.add(sourceNode.id);
            return;
          }
          movedNodeIds.add(sourceNode.id);
        }
      });

      // Now position all source nodes after the target node, maintaining their relative order
      const targetIndex = targetParent.children.findIndex((child: ISceneTreeNode) => child.id === targetNode.id);

      if (targetIndex !== -1) {
        runInAction(() => {
          // Get current indices of all source nodes and sort them to maintain relative order
          const sourceIndicesWithNodes = validSourceNodes
            .map(sourceNode => ({
              node: sourceNode,
              currentIndex: targetParent.children.findIndex((child: ISceneTreeNode) => child.id === sourceNode.id)
            }))
            .filter(item => item.currentIndex !== -1)
            .sort((a, b) => a.currentIndex - b.currentIndex);

          // Calculate the final target position (right after the target node)
          let insertPosition = targetParent.children.findIndex((child: ISceneTreeNode) => child.id === targetNode.id) + 1;

          // Move each source node one by one, recalculating indices each time
          sourceIndicesWithNodes.forEach((item, index) => {
            // Find the current position of this node (it may have changed due to previous moves)
            const currentIndex = targetParent.children.findIndex((child: ISceneTreeNode) => child.id === item.node.id);
            if (currentIndex !== -1) {
              // Calculate where we want this specific item to end up
              // It should be at insertPosition + index (to maintain order)
              const desiredPosition = insertPosition + index;
              const adjustedDesiredPosition = adjustMoveInsertIndex(currentIndex, desiredPosition);

              if (currentIndex !== adjustedDesiredPosition) {
                moveItem(targetParent.children, currentIndex, adjustedDesiredPosition);
                movedNodeIds.add(item.node.id);

                // Update insertPosition for next iteration if needed
                // If we moved an item from before insertPosition to after, insertPosition shifts left
                if (currentIndex < insertPosition) {
                  insertPosition--;
                }
                this.bumpVersion();
              }
            }
          });
        });
      }
      return {
        movedNodeIds: Array.from(movedNodeIds),
        skippedNodeIds: Array.from(skippedNodeIds),
      };
    },
    /** Get actors in a folder */
    getActorsInFolder(folderIdOrNode: string | ISceneTreeNode): string[] {
      const folder = typeof folderIdOrNode === 'string' ? self.getNodeById(folderIdOrNode) : folderIdOrNode;
      if (!folder || !folder.isFolder) return [];

      return folder.children
        .filter(child => child.nodeType === 'actor')
        .map(child => child.id);
    },
    /** Get the folder containing an actor */
    getActorFolder(actorId: string): string | null {
      const actorNode = self.getNodeById(actorId);
      if (!actorNode || actorNode.nodeType !== 'actor') return null;

      const findParentFolder = (node: ISceneTreeNode, targetId: string): string | null => {
        for (const child of node.children) {
          if (child.id === targetId && child.nodeType === 'actor') {
            return node.nodeType === 'folder' ? node.id : null;
          }
          const result = findParentFolder(child, targetId);
          if (result) return result;
        }
        return null;
      };

      const rootNode = self.rootNode;
      return rootNode ? findParentFolder(rootNode, actorId) : null;
    },
    /** Generate unique folder name */
    generateUniqueFolderName(baseName: string = 'Folder'): string {
      const rootNode = self.rootNode;
      if (!rootNode) return baseName;

      const existingNames = new Set<string>();
      const collectNames = (node: ISceneTreeNode) => {
        if (node.nodeType === 'folder' && node.name) {
          existingNames.add(node.name);
        }
        node.children.forEach(collectNames);
      };
      collectNames(rootNode);

      let counter = 1;
      let candidateName = baseName;
      while (existingNames.has(candidateName)) {
        candidateName = `${baseName} ${counter}`;
        counter++;
      }
      return candidateName;
    }
  }));
export interface ISceneTree extends Instance<typeof SceneTree> { }
